import { CategoryInfo } from "@/components/CategoryInfo";
import { Icon } from "@/components/Icon";
import { groupCategories } from "@/lib/categoryGroups";
import { cn } from "@/lib/utils";
import { tg } from "@/lib/telegram";

/**
 * Вибір сфер — ОДИН компонент на всі екрани (owner 2026-08-29: «різне
 * оформлення»). До цього список жив трьома копіями розмітки — у профілі,
 * на сторінці «Твої сфери» і в онбордингу — і виглядав по-різному: там
 * сірі плитки й таблетка «ПРИБРАТИ ВСІ», тут білі картки й текстове
 * «Зняти всі». Тепер вигляд заданий тут, і розійтись він не може.
 *
 * Групи (Дизайн / Розробка / Фото і відео / …) — з lib/categoryGroups.ts.
 */

export type SphereItem = { id: number; slug: string; name_uk: string };

interface Props {
  items: SphereItem[];
  selected: Set<number>;
  onToggle: (id: number) => void;
  onSelectAll: () => void;
  onClear: () => void;
}

export function SpheresPicker({
  items,
  selected,
  onToggle,
  onSelectAll,
  onClear,
}: Props) {
  const groups = groupCategories(items);
  const allSelected = selected.size === items.length && items.length > 0;

  return (
    <>
      {/* Лічильник потрібен під час вибору, тому стоїть НАД списком
          (owner 2026-08-23). */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className="font-display text-[0.8125rem] font-semibold text-ink">
          Обрано <span className="tabular-nums">{selected.size}</span> з{" "}
          <span className="tabular-nums">{items.length}</span>
        </span>
        <button
          type="button"
          onClick={() => {
            tg()?.HapticFeedback?.selectionChanged?.();
            if (allSelected) onClear();
            else onSelectAll();
          }}
          className={cn(
            "shrink-0 font-display text-[0.8125rem] font-semibold active:opacity-60",
            allSelected ? "text-ink-dim" : "text-primary",
          )}
        >
          {allSelected ? "Зняти всі" : "Обрати всі"}
        </button>
      </div>

      {groups.map((group) => (
        <div key={group.title} className="mb-4 last:mb-0">
          <div className="mb-1.5 font-display text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-ink-dim">
            {group.title}
          </div>
          <div className="overflow-hidden rounded-ios-card bg-surface px-1.5 py-1 shadow-card-soft">
            {group.items.map((c, i) => {
              const isLast = i === group.items.length - 1;
              const isSel = selected.has(c.id);
              return (
                <div
                  key={c.id}
                  className={cn(
                    "flex items-center gap-2 pr-4 transition-colors",
                    !isLast && "border-b border-line",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onToggle(c.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-4 py-3 text-left active:bg-surface-soft"
                  >
                    <div
                      className={cn(
                        "grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 transition",
                        isSel ? "border-primary bg-primary" : "border-ink/20 bg-bg",
                      )}
                    >
                      {isSel && (
                        <Icon name="check" size={14} color="#FFFFFF" />
                      )}
                    </div>
                    <div
                      className={cn(
                        // Owner 2026-09-08: назви сфер були завеликі й
                        // «важкі» — рядок читається як заголовок, а це просто
                        // пункт списку. Зменшено 0.90625 → 0.8125rem, а
                        // вибраний варіант — з bold на semibold.
                        "flex-1 font-display text-[0.8125rem] leading-tight",
                        isSel
                          ? "font-semibold text-ink"
                          : "font-medium text-ink-dim",
                      )}
                    >
                      {c.name_uk}
                    </div>
                  </button>
                  {/* Інфо-кружечок «?» — пояснення що входить у сферу */}
                  <CategoryInfo slug={c.slug} name={c.name_uk} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}
